import { QueryClient } from '@tanstack/react-query';
import { ApiError, ContractViolationError } from './api/http';
// Type-only, and therefore erased: `ops-queries` imports `queryKeys` from here at runtime, so a
// value import back would close a cycle. The union is declared beside the hook that sends it.
import type { MetricsRange } from './api/ops-queries';

/**
 * The shared query client.
 *
 * The retry policy is the interesting part. SAD §8 gives every error a `retryable` flag, and the
 * console honours it rather than blindly retrying three times: a `409 ILLEGAL_TRANSITION` or a
 * `403` will never succeed on a second attempt, and retrying them just delays the message the
 * operator needs to see. A contract violation is never retried either — it is a bug, not a blip.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ContractViolationError) return false;
          if (error instanceof ApiError) return error.retryable && failureCount < 3;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Query keys, centralised so an invalidation can never miss a cache entry through a typo.
 *
 * Grouped by backend, mirroring D-I7's two areas.
 */
export const queryKeys = {
  session: ['session'] as const,
  ops: {
    metrics: ['ops', 'metrics'] as const,
    // One cache entry per KPI window; invalidating the `metrics` prefix clears every window.
    //
    // The discriminant is part of the key, not just the numbers: a preset and a custom range are
    // different questions with different answers, and a key of `['ops', 'metrics', 24]` for both
    // would serve one selection's payload to the other. Spelling the bounds out keeps two custom
    // ranges apart too.
    metricsWindow: (range: MetricsRange) =>
      range.kind === 'preset'
        ? (['ops', 'metrics', 'preset', range.hours] as const)
        : (['ops', 'metrics', 'custom', range.from, range.to] as const),
    // The amount histogram: a *different question* over the same window, so a sibling key rather
    // than a deeper `metrics` one — it is fetched, cached and invalidated independently, and a
    // dashboard that blanked its KPIs because the histogram 500'd would be trading a working
    // screen for a broken one. It varies with the selection exactly the way `metricsWindow` does,
    // for exactly the same reason: a preset and a custom range are not interchangeable answers.
    amountDistribution: (range: MetricsRange) =>
      range.kind === 'preset'
        ? (['ops', 'amount-distribution', 'preset', range.hours] as const)
        : (['ops', 'amount-distribution', 'custom', range.from, range.to] as const),
    // The per-deposit scatter: a *third* question over the same window, so a third sibling key.
    // It is by far the largest of the three payloads and the one most worth evicting on its own,
    // which is exactly what a separate key buys — and, as above, a preset and a custom range are
    // not interchangeable answers, so both discriminant and bounds are part of the key.
    amountScatter: (range: MetricsRange) =>
      range.kind === 'preset'
        ? (['ops', 'amount-scatter', 'preset', range.hours] as const)
        : (['ops', 'amount-scatter', 'custom', range.from, range.to] as const),
    reviewQueue: (filters: Record<string, string>) => ['ops', 'review-queue', filters] as const,
    // The unified lookup (`/ops/lookup`). Keyed on the query text alone — there is no page, sort
    // or status to add, and `webScopedKey` appends the website scope the same way every other
    // scoped read here does.
    lookup: (q: string) => ['ops', 'lookup', q] as const,
    reviewCase: (id: string) => ['ops', 'review-case', id] as const,
    reviewCaseContext: (id: string) => ['ops', 'review-case-context', id] as const,
    transaction: (id: string) => ['ops', 'transaction', id] as const,
    evidenceFile: (transactionId: string) => ['ops', 'evidence-file', transactionId] as const,
    participants: ['ops', 'participants'] as const,
    operators: ['ops', 'operators'] as const,
    keys: (participantId: string) => ['ops', 'keys', participantId] as const,
    payeeAccounts: ['ops', 'payee-accounts'] as const,
    dlq: ['ops', 'dlq'] as const,
    policy: ['ops', 'policy'] as const,
    // Slip-verification quota per provider. Platform-wide, not per-window and not per-filter —
    // PaymentFlow holds one vendor account per provider, not one per website, so there is nothing
    // to key this on beyond the resource itself. See `useProviderQuota` in `lib/api/ops-queries.ts`.
    providerQuota: ['ops', 'provider-quota'] as const,
    clearing: ['ops', 'clearing'] as const,
    settlements: ['ops', 'settlements'] as const,
    // Recon-feed polling health (2026-08-27 observability pair). One cache entry per scope —
    // there is no window or filter beyond the website scope every other list here already keys on.
    reconHealth: ['ops', 'recon-health'] as const,
    // Ledger divergences a participant's own reconciliation reported. `filters` carries `since`
    // and `limit` because the dashboard's 24h count and the full list page ask the same endpoint
    // two different questions — a stale count cached under the list's key (or vice versa) would
    // silently show one screen the other's answer.
    ledgerDivergences: (filters: Record<string, string>) =>
      ['ops', 'ledger-divergences', filters] as const,
    scenario: ['ops', 'scenario'] as const,
    withdrawalQueue: (filters: Record<string, string>) =>
      ['ops', 'withdrawal-queue', filters] as const,
    // Split-fill lineage for one withdrawal (migration 0034). Keyed on the id the caller passed
    // (a parent's or a slice's) rather than the root the server resolves to, so the cache slot
    // matches the query that was actually made — the console never sees the root id up front.
    withdrawalLineage: (id: string) => ['ops', 'withdrawal-lineage', id] as const,
    transactionHistory: (filters: Record<string, string>) =>
      ['ops', 'transaction-history', filters] as const,
    // The depositor abandonment leaderboard. A sibling of `metrics` rather than a child of it,
    // even though it is windowed the same way: it is a *list* with its own paging and filter, it
    // fails and retries on its own, and nothing should invalidate it as a side effect of
    // refreshing the KPIs. The window is inside `filters` with everything else, because every one
    // of those params changes which rows come back.
    depositorAbandonment: (filters: Record<string, string>) =>
      ['ops', 'depositor-abandonment', filters] as const,
    // The shadow-reviewer agreement report — a sibling of `metrics` for the same reason
    // `depositorAbandonment` is: its own query, its own failure, nothing should invalidate it as
    // a side effect of anything else. Discriminant plus bounds, exactly like `metricsWindow`,
    // since a preset and a custom range are different questions here too.
    // `reviewer` is part of the key, not a client-side filter: narrowing to one model changes
    // every number in the response (the rates, `failed`, `uncompared`), so two selections are two
    // different server answers and must not share a cache slot. `null` — no narrowing — is its
    // own slot, the mixed view the API flags with `model: null`.
    aiReviewAgreement: (range: MetricsRange, reviewer: { model: string; promptVersion: string } | null) =>
      range.kind === 'preset'
        ? ([
            'ops',
            'ai-review-agreement',
            'preset',
            range.hours,
            reviewer?.model ?? null,
            reviewer?.promptVersion ?? null,
          ] as const)
        : ([
            'ops',
            'ai-review-agreement',
            'custom',
            range.from,
            range.to,
            reviewer?.model ?? null,
            reviewer?.promptVersion ?? null,
          ] as const),
    // The disagreement list. A sibling key rather than a child of `aiReviewAgreement`, in the same
    // shape `depositorAbandonment` uses: it is a paginated list with its own failure mode (see the
    // page's "disagreements failing must not blank the agreement table" test), so the two must be
    // able to invalidate and error independently.
    aiReviewDisagreements: (range: MetricsRange, page: number, limit: number) =>
      range.kind === 'preset'
        ? (['ops', 'ai-review-disagreements', 'preset', range.hours, page, limit] as const)
        : (['ops', 'ai-review-disagreements', 'custom', range.from, range.to, page, limit] as const),
  },
  bff: {
    balance: ['bff', 'balance'] as const,
    depositOptions: ['bff', 'deposit-options'] as const,
    activeDeposit: ['bff', 'active-deposit'] as const,
    deposit: (id: string) => ['bff', 'deposit', id] as const,
    withdrawals: ['bff', 'withdrawals'] as const,
    withdrawal: (id: string) => ['bff', 'withdrawal', id] as const,
    activity: (filters: Record<string, string>) => ['bff', 'activity', filters] as const,
  },
} as const;
